import {ResourceType} from "models/ResourceType.ts";

export class EditorStorage {
    #editor;
    #lessonId;
    #ResourceTypeConfig = {
        [ResourceType.NodeVideo]: {
            files: [],
            sortedOptions: [],
            groups: [],
            tempSelectedGroup: null,
            localStoragePrefix: "videoUploadSelectedGroup"
        },
        [ResourceType.NodeImage]: {
            files: [],
            sortedOptions: [],
            groups: [],
            tempSelectedGroup: null,
            localStoragePrefix: "imageUploadSelectedGroup"
        },
        [ResourceType.NodeAudio]: {
            files: [],
            sortedOptions: [],
            groups: [],
            tempSelectedGroup: null,
            localStoragePrefix: "audioUploadSelectedGroup"
        },
        [ResourceType.NodeInfoIcon]: {
            files: [],
            sortedOptions: [],
            groups: [],
            tempSelectedGroup: null,
            localStoragePrefix: "infoIconUploadSelectedGroup"
        },
        [ResourceType.AvatarAnimation]: {
            files: [],
            sortedOptions: [],
            groups: [],
            tempSelectedGroup: null,
            localStoragePrefix: "avatarAnimationUploadSelectedGroup"
        }
    };

    constructor(editor, lessonId) {
        this.#editor = editor;
        this.#lessonId = lessonId;
        this.#editor.bind("changeNodeFilesList");
        this.#editor.bind("changeNodeFilesGroups");
        this.#editor.bind("removedNodeFile");
    }

    #validateResourceType(resourceType) {
        const config = this.#ResourceTypeConfig[resourceType];
        if (!config) {
            console.error("Unhandled node file type!");
            return null;
        }
        return config;
    }

    #createSortedOptions(nodeFiles) {
        return nodeFiles
            .map(nodeFile => ({
                value: nodeFile.id,
                label: nodeFile.fileName,
                group: nodeFile.group ?? ""
            }))
            .sort((a, b) => a.group.localeCompare(b.group));
    }

    #getLocalStorageKey(resourceType) {
        const config = this.#ResourceTypeConfig[resourceType];
        return config && this.#lessonId
            ? `${config.localStoragePrefix}_${this.#lessonId}`
            : null;
    }

    #getLocalStorageSelectedGroup(resourceType) {
        const key = this.#getLocalStorageKey(resourceType);
        return key ? localStorage.getItem(key) : null;
    }

    #setLocalStorageSelectedGroup(selectedGroup, resourceType) {
        const key = this.#getLocalStorageKey(resourceType);
        if (key) {
            if (selectedGroup) {
                localStorage.setItem(key, selectedGroup);
            } else {
                localStorage.removeItem(key);
            }
        }
    }

    #getTempStorageSelectedGroup(resourceType) {
        const config = this.#ResourceTypeConfig[resourceType];
        return config?.tempSelectedGroup ?? null;
    }

    #setTempStorageSelectedGroup(selectedGroup, resourceType) {
        const config = this.#ResourceTypeConfig[resourceType];
        if (config) {
            config.tempSelectedGroup = selectedGroup;
        }
    }

    setNodeFiles(resourceType, newNodeFiles) {
        const config = this.#validateResourceType(resourceType);
        if (!config) return;

        if (config.files !== newNodeFiles) {
            config.files = newNodeFiles;
            config.sortedOptions = this.#createSortedOptions(newNodeFiles);

            this.#editor.trigger("changeNodeFilesList", {
                resourceType,
                data: newNodeFiles,
                sortedOptions: config.sortedOptions
            });
        }
    }

    setNodeFileGroups(resourceType, newNodeFileGroups) {
        const config = this.#validateResourceType(resourceType);
        if (!config) return;

        if (config.groups !== newNodeFileGroups) {
            config.groups = newNodeFileGroups;
            this.#editor.trigger("changeNodeFilesGroups", {
                resourceType,
                newGroups: newNodeFileGroups
            });
        }
    }

    setNodeFileRemoved(resourceType, fileId) {
        const config = this.#validateResourceType(resourceType);
        if (!config) return;

        this.#editor.trigger("removedNodeFile", {resourceType, fileId});
    }

    getSelectedGroup(resourceType) {
        return this.#lessonId
            ? this.#getLocalStorageSelectedGroup(resourceType)
            : this.#getTempStorageSelectedGroup(resourceType);
    }

    saveSelectedGroup(resourceType, selectedGroup) {
        if (this.#lessonId) {
            this.#setLocalStorageSelectedGroup(selectedGroup, resourceType);
        } else {
            this.#setTempStorageSelectedGroup(selectedGroup, resourceType);
        }
    }

    saveTempSelectedGroup(newLessonId) {
        Object.values(this.#ResourceTypeConfig).forEach(config => {
            if (config.tempSelectedGroup) {
                const key = `${config.localStoragePrefix}_${newLessonId}`;
                if (!localStorage.getItem(key)) {
                    localStorage.setItem(key, config.tempSelectedGroup);
                }
            }
        });
    }

    getSortedOptions(resourceType) {
        return this.#ResourceTypeConfig[resourceType]?.sortedOptions ?? [];
    }

    getGroups(resourceType) {
        return this.#ResourceTypeConfig[resourceType]?.groups ?? [];
    }
}